import React, { useState, useEffect } from 'react';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { ResizableTable, ResizableHeader } from '../components/ResizableTable';
import { useSearchParams, useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import Pagination from '../components/Pagination';
import { validateTestSuiteName, getCharacterCountDisplay } from '../utils/nameValidator';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function TestSuites() {
  const [searchParams] = useSearchParams();
  const { projectKey: routeProjectKey } = useParams();
  // Support both URL params (/projects/:projectKey/test-suites) and query params (?projectKey=...)
  const projectKey = routeProjectKey || searchParams.get('projectKey');
  const [projectId, setProjectId] = useState(null);
  const [suites, setSuites] = useState([]);
  const [project, setProject] = useState(null);
  const [testCaseCounts, setTestCaseCounts] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [editMode, setEditMode] = useState(false);
  const [editingSuiteId, setEditingSuiteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedSuites, setSelectedSuites] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);

  const SUITE_COLS = [
    { key: 'check',      defaultWidth: 36 },
    { key: 'name',       defaultWidth: 220 },
    { key: 'description',defaultWidth: 300 },
    { key: 'cases',      defaultWidth: 110 },
    { key: 'created',    defaultWidth: 120 },
    { key: 'actions',    defaultWidth: 120 },
  ];
  const { colWidths: suiteColWidths, onMouseDown: suiteMouseDown, tableRef: suiteTableRef } = useResizableColumns('zipqa-testsuites-cols', SUITE_COLS);

  useEffect(() => {
    if (projectKey) {
      setCurrentPage(1);
      fetchSuites();
      fetchProject();
    }
  }, [projectKey]);

  useEffect(() => {
    if ((currentPage > 1 || pageSize !== 50) && projectKey) {
      fetchSuites();
    }
  }, [currentPage, pageSize]);

  const fetchProject = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/projects/${projectKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const projectData = data.data || data;
        if (projectData) {
          setProject(projectData);
          setProjectId(projectData.id || projectData._id);
        }
      }
    } catch (err) {
      console.error('Error loading project:', err);
    }
  };

  const fetchSuites = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: pageSize };
      if (projectId) params.projectId = projectId;
      const response = await api.get('/suites', { params });
      setSuites(response.data.data || response.data.testSuites || []);
      if (response.data.pagination) {
        setTotalItems(response.data.pagination.total || 0);
        setTotalPages(response.data.pagination.pages || 1);
      }

      // Fetch test case counts for each suite
      const counts = {};
      const suitesData = response.data.data || response.data.testSuites || [];
      await Promise.all(
        suitesData.map(async (suite) => {
          try {
            const casesResponse = await api.get(`/testcases?suiteId=${suite._id || suite.id}&limit=0`);
            counts[suite._id || suite.id] = casesResponse.data.pagination?.total || 0;
          } catch {
            counts[suite._id || suite.id] = 0;
          }
        })
      );
      setTestCaseCounts(counts);
      setError('');
    } catch (err) {
      setError('Failed to fetch test suites: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = (name) => {
    setFormData({ ...formData, name });
    
    // Real-time validation
    if (name.trim().length > 0) {
      const validation = validateTestSuiteName(name, { normalize: false });
      setValidationErrors(validation.errors);
      setValidationWarnings(validation.warnings);
    } else {
      setValidationErrors([]);
      setValidationWarnings([]);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    
    // Validate name with normalization
    const validation = validateTestSuiteName(formData.name, { normalize: true });
    
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      setError(validation.errors.join('; '));
      return;
    }
    
    const trimmedName = validation.normalized;
    
    try {
      const submitData = {
        ...formData,
        name: trimmedName
      };
      
      if (editMode) {
        await api.put(`/suites/${editingSuiteId}`, submitData);
        setEditMode(false);
        setEditingSuiteId(null);
      } else {
        await api.post('/suites', submitData);
        // Dispatch event to refresh sidebar
        window.dispatchEvent(new CustomEvent('refreshSidebar', { detail: { type: 'testSuites' } }));
      }
      setFormData({ name: '', description: '' });
      setShowModal(false);
      fetchSuites();
      setError('');
    } catch (err) {
      const errorData = err.response?.data;
      let errorMessage = 'Failed to save test suite: ' + (errorData?.error || errorData?.message || err.message);
      if (errorData?.details) {
        errorMessage += ' - ' + errorData.details;
      }
      setError(errorMessage);
    }
  };

  const handleEdit = suite => {
    setFormData({
      name: suite.name,
      description: suite.description || '',
    });
    setEditMode(true);
    setEditingSuiteId(suite._id || suite.id);
    setShowModal(true);
  };

  const handleDelete = async id => {
    const count = testCaseCounts[id] || 0;
    const message =
      count > 0
        ? `This suite contains ${count} test case(s). Are you sure you want to delete it?`
        : 'Are you sure you want to delete this test suite?';

    if (window.confirm(message)) {
      try {
        await api.delete(`/suites/${id}`);
        fetchSuites();
        // Dispatch event to refresh sidebar
        window.dispatchEvent(new CustomEvent('refreshSidebar', { detail: { type: 'testSuites' } }));
        setError('');
      } catch (err) {
        setError('Failed to delete test suite: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const handleCancel = () => {
    setFormData({ name: '', description: '' });
    setEditMode(false);
    setEditingSuiteId(null);
    setShowModal(false);
    setValidationErrors([]);
    setValidationWarnings([]);
    setError('');
  };

  const openCreateModal = () => {
    setFormData({ name: '', description: '' });
    setEditMode(false);
    setEditingSuiteId(null);
    setShowModal(true);
    setValidationErrors([]);
    setValidationWarnings([]);
    setError('');
  };

  const handleBulkDelete = async () => {
    if (selectedSuites.length === 0) return;

    const hasCases = selectedSuites.some(id => (testCaseCounts[id] || 0) > 0);
    let message = `Delete ${selectedSuites.length} test suite(s)?`;
    if (hasCases) {
      message = `${selectedSuites.length} suite(s) contain test cases. Are you sure you want to delete them?`;
    }

    if (!window.confirm(message)) return;

    try {
      for (const suiteId of selectedSuites) {
        await api.delete(`/suites/${suiteId}`);
      }
      setSelectedSuites([]);
      fetchSuites();
      // Dispatch event to refresh sidebar
      window.dispatchEvent(new CustomEvent('refreshSidebar', { detail: { type: 'testSuites' } }));
      setError('');
    } catch (err) {
      setError('Failed to delete test suites: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSelectSuite = (suiteId) => {
    setSelectedSuites(prev =>
      prev.includes(suiteId) ? prev.filter(id => id !== suiteId) : [...prev, suiteId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSuites.length === suites.length) {
      setSelectedSuites([]);
    } else {
      setSelectedSuites(suites.map(s => s._id || s.id));
    }
  };

  return (
    <div className="container mt-4">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0" style={{ background: 'transparent', padding: 0 }}>
          <li className="breadcrumb-item">
            <Link to="/projects">Projects</Link>
          </li>
          {projectKey && project && (
            <li className="breadcrumb-item">
              <Link to={`/projects/${projectKey.toLowerCase()}`}>{project?.name}</Link>
            </li>
          )}
          <li className="breadcrumb-item active">Test Suites</li>
        </ol>
      </nav>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ marginTop: '0.5rem' }}>
          Test Suites {selectedSuites.length > 0 && `(${selectedSuites.length} selected)`}
        </h2>
        <div>
          {selectedSuites.length > 0 && (
            <button className="btn btn-danger me-2" onClick={handleBulkDelete}>
              Delete Selected
            </button>
          )}
          <button className="btn btn-primary" onClick={openCreateModal}>
            Create Test Suite
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editMode ? 'Edit Test Suite' : 'Create Test Suite'}
                </h5>
                <button type="button" className="btn-close" onClick={handleCancel}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      className={`form-control ${validationErrors.length > 0 ? 'is-invalid' : ''}`}
                      maxLength="200"
                      value={formData.name}
                      onChange={e => handleNameChange(e.target.value)}
                      placeholder="Enter suite name"
                      required
                    />
                    <small className="form-text text-muted d-block mt-1">
                      Max 200 characters. Letters, numbers, spaces, and common punctuation (- / &amp; . , etc.) are allowed.
                    </small>
                    {formData.name.length > 0 && (
                      <small className={getCharacterCountDisplay(formData.name.length, 200).className}>
                        {getCharacterCountDisplay(formData.name.length, 200).text}
                      </small>
                    )}
                    {validationWarnings.length > 0 && (
                      <div className="alert alert-warning mt-2 py-2 px-3" style={{ fontSize: '0.875rem' }}>
                        {validationWarnings.map((warning, idx) => (
                          <div key={idx}>• {warning}</div>
                        ))}
                      </div>
                    )}
                    {validationErrors.length > 0 && (
                      <div className="invalid-feedback d-block">
                        {validationErrors.map((error, idx) => (
                          <div key={idx}>• {error}</div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      value={formData.description}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Enter description"
                      rows="3"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editMode ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Test Suites List</h5>

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : suites.length === 0 ? (
            <p className="text-muted">No test suites found. Create one to get started.</p>
          ) : (
            <>
            <ResizableTable tableRef={suiteTableRef} className="table-hover">
                <thead>
                  <tr>
                    <ResizableHeader colKey="check" width={suiteColWidths.check} onMouseDown={suiteMouseDown}>
                      <input
                        type="checkbox"
                        checked={selectedSuites.length === suites.length && suites.length > 0}
                        onChange={handleSelectAll}
                        className="form-check-input"
                      />
                    </ResizableHeader>
                    <ResizableHeader colKey="name" width={suiteColWidths.name} onMouseDown={suiteMouseDown}>Name</ResizableHeader>
                    <ResizableHeader colKey="description" width={suiteColWidths.description} onMouseDown={suiteMouseDown}>Description</ResizableHeader>
                    <ResizableHeader colKey="cases" width={suiteColWidths.cases} onMouseDown={suiteMouseDown}>Test Cases</ResizableHeader>
                    <ResizableHeader colKey="created" width={suiteColWidths.created} onMouseDown={suiteMouseDown}>Created</ResizableHeader>
                    <ResizableHeader colKey="actions" width={suiteColWidths.actions} onMouseDown={suiteMouseDown}>Actions</ResizableHeader>
                  </tr>
                </thead>
                <tbody>
                  {suites.map(suite => (
                    <tr key={suite._id || suite.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedSuites.includes(suite._id || suite.id)}
                          onChange={() => handleSelectSuite(suite._id || suite.id)}
                          className="form-check-input"
                        />
                      </td>
                      <td className="cell-wrap">
                        <Link
                          to={`/projects/${projectKey}/test-suites/${suite._id || suite.id}`}
                          style={{ textDecoration: 'none', fontWeight: 'bold' }}
                        >
                          {suite.name}
                        </Link>
                      </td>
                      <td className="cell-wrap">{suite.description || <em className="text-muted">No description</em>}</td>
                      <td>
                        <span className="badge bg-info">
                          {testCaseCounts[suite._id || suite.id] || 0} cases
                        </span>
                      </td>
                      <td>
                        <small className="text-muted">
                          {new Date(suite.createdAt).toLocaleDateString()}
                        </small>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleEdit(suite)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(suite._id || suite.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ResizableTable>

              {/* Pagination */}
              {suites.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  limit={pageSize}
                  total={totalItems}
                  onPageChange={setCurrentPage}
                  onLimitChange={(newLimit) => {
                    setPageSize(newLimit);
                    setCurrentPage(1);
                  }}
                  isLoading={loading}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default TestSuites;
